import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listInvoices,
  openInvoicePdf,
  markInvoicePaid,
  InvoiceListItem,
} from "../../../lib/adminApi";
import { useToast } from "../../../components/Toast";

type PaidFilter = "all" | "unpaid" | "paid";

export default function InvoicesList() {
  const toast = useToast();
  const [filter, setFilter] = useState<PaidFilter>("unpaid");
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [loading, setLoading] = useState(true);

  function reload() {
    setLoading(true);
    const paid = filter === "paid" ? true : filter === "unpaid" ? false : undefined;
    listInvoices(paid)
      .then(setInvoices)
      .catch((e) => toast.show("error", e.message))
      .finally(() => setLoading(false));
  }
  useEffect(reload, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onOpenPdf(id: string) {
    try {
      await openInvoicePdf(id);
    } catch (e: any) {
      toast.show("error", e.message);
    }
  }

  async function onTogglePaid(inv: InvoiceListItem) {
    try {
      await markInvoicePaid(inv.id, !inv.paid);
      toast.show("success", !inv.paid ? "Označeno jako zaplacené." : "Označeno jako nezaplacené.");
      reload();
    } catch (e: any) {
      toast.show("error", e.message);
    }
  }

  const totalUnpaid = invoices.filter((i) => !i.paid).reduce((s, i) => s + i.totalCzk, 0);

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900">Faktury</h1>
      <p className="mt-1 text-slate-600">
        Vystavené faktury. Klik → PDF se otevře v novém okně.
      </p>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {(["unpaid", "paid", "all"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              filter === f
                ? "bg-obloha-indigo-700 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {f === "unpaid" ? "Nezaplacené" : f === "paid" ? "Zaplacené" : "Všechny"}
          </button>
        ))}
      </div>

      {filter === "unpaid" && totalUnpaid > 0 && (
        <div className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          Celkem nezaplaceno: <strong>{totalUnpaid.toLocaleString("cs-CZ")} Kč</strong>
        </div>
      )}

      {loading ? (
        <div className="mt-8 text-slate-500">Načítám…</div>
      ) : invoices.length === 0 ? (
        <div className="card mt-8 text-center text-slate-500">
          Žádné faktury v tomto filtru.
        </div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Číslo</th>
                <th className="px-4 py-3 text-left">Odběratel</th>
                <th className="px-4 py-3 text-left">Vystavení</th>
                <th className="px-4 py-3 text-left">Splatnost</th>
                <th className="px-4 py-3 text-right">Částka</th>
                <th className="px-4 py-3 text-center">Stav</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {invoices.map((inv) => {
                const overdue = !inv.paid && new Date(inv.dateDue) < new Date();
                return (
                  <tr key={inv.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-medium">{inv.number}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">
                        {inv.reservation.snapshotName}
                      </div>
                      <Link
                        to={`/admin/reservations/${inv.reservation.id}`}
                        className="text-xs text-obloha-indigo-700 hover:underline"
                      >
                        → rezervace
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {new Date(inv.dateIssued).toLocaleDateString("cs-CZ")}
                    </td>
                    <td className="px-4 py-3">
                      <span className={overdue ? "font-medium text-red-600" : "text-slate-600"}>
                        {new Date(inv.dateDue).toLocaleDateString("cs-CZ")}
                        {overdue && <span className="ml-1">⚠️</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">
                      {inv.totalCzk.toLocaleString("cs-CZ")} Kč
                    </td>
                    <td className="px-4 py-3 text-center">
                      {inv.paid ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                          Zaplaceno
                        </span>
                      ) : (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          Čeká
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => onOpenPdf(inv.id)}
                          className="text-sm font-medium text-obloha-indigo-700 hover:text-obloha-indigo-900"
                        >
                          PDF
                        </button>
                        <span className="text-slate-300">·</span>
                        <button
                          onClick={() => onTogglePaid(inv)}
                          className="text-sm font-medium text-slate-600 hover:text-slate-900"
                        >
                          {inv.paid ? "Odznačit" : "✓ Zaplaceno"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
